import { useEffect, useId, useRef, useState } from 'react';

interface ConversationRibbonProps {
  className?: string;
  /** Decorative motion belongs only on the public hero, never in an assessment. */
  animated?: boolean;
}

/** Brand ornament, not a waveform or an indication of provider/device activity. */
export default function ConversationRibbon({ className = '', animated = false }: ConversationRibbonProps) {
  const id = useId().replace(/:/g, '');
  const root = useRef<HTMLDivElement>(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!animated) return;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let visible = false;
    const update = () => setRunning(visible && !document.hidden && !motion.matches);
    // Leave a graceful static ribbon when visibility observation is unavailable.
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(entries => {
      visible = entries.some(entry => entry.isIntersecting);
      update();
    });
    if (root.current) observer?.observe(root.current);
    document.addEventListener('visibilitychange', update);
    motion.addEventListener('change', update);
    return () => {
      observer?.disconnect();
      document.removeEventListener('visibilitychange', update);
      motion.removeEventListener('change', update);
    };
  }, [animated]);

  return <div ref={root} className={`conversation-ribbon ${className}`.trim()} aria-hidden="true" data-running={animated && running}>
    <svg viewBox="0 0 1000 620" fill="none" focusable="false">
      <defs>
        <linearGradient id={`ribbon-${id}`} x1="50" y1="450" x2="950" y2="120" gradientUnits="userSpaceOnUse">
          <stop className="ribbon-stop-cyan" />
          <stop offset=".4" className="ribbon-stop-blue" />
          <stop offset=".76" className="ribbon-stop-indigo" />
          <stop offset="1" className="ribbon-stop-violet" />
        </linearGradient>
      </defs>
      <g className="conversation-ribbon__flow" stroke={`url(#ribbon-${id})`}>
        {Array.from({ length: 11 }, (_, index) => <path key={index}
          d={`M -100 ${410 + index * 10} C 135 ${580 - index * 9}, 250 ${80 + index * 6}, 470 ${230 + index * 8} S 760 ${570 - index * 9}, 1100 ${45 + index * 10}`}
          strokeWidth={index === 5 ? 2 : 1} opacity={.2 + (5 - Math.abs(5 - index)) * .1} />)}
      </g>
    </svg>
  </div>;
}
